import type { Article } from "../../types/article";
import { CATEGORY_LABELS } from "../../types/article";
import { KebabMenu } from "../KebabMenu";

interface ArticleCardProps {
  article: Article;
  onEdit: () => void;
  onDelete: () => void;
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
}

export function ArticleCard({ article, onEdit, onDelete }: ArticleCardProps) {
  const coverSrc = article.cover_url ?? article.cover_thumb_url;

  return (
    <div className="relative w-full h-[200px] rounded-xl overflow-hidden group">
      {coverSrc ? (
        <img
          src={`${coverSrc}${coverSrc.includes("?") ? "&" : "?"}v=${encodeURIComponent(article.updated_at)}`}
          alt={article.title}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-bg-secondary" />
      )}

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-card-overlay-start to-transparent" />

      {/* Kebab menu */}
      <div className="absolute top-2 right-2">
        <KebabMenu onEdit={onEdit} onDelete={onDelete} />
      </div>

      {/* Text content */}
      <div className="absolute bottom-3 left-3 right-3 text-text-btn">
        <p className="text-xs font-medium leading-[1.2] opacity-80">
          {timeAgo(article.created_at)}
        </p>
        <h3 className="text-xl font-semibold leading-tight truncate mt-1">
          {article.title}
        </h3>
        <p className="text-xs font-medium leading-[1.2] mt-1 opacity-80">
          {CATEGORY_LABELS[article.category] ?? article.category} | {article.read_time_minutes} min
        </p>
      </div>
    </div>
  );
}
